import { useCallback, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { PageHeader } from '@/components/ui/page-header';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { optStr, optPage, optEnum } from '@/lib/list-search';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { ViewModeToggle, type ViewMode } from '@/components/ui/view-mode-toggle';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Pagination } from '@/components/ui/pagination';
import {
  Plus,
  Upload,
  Eye,
  Edit,
  Trash2,
  History,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Terminal,
} from 'lucide-react';
import { usePermissions } from '@/common/hooks/usePermissions';
import { useNotification } from '@/hooks/useNotification';
import { assetsService, Asset } from '@/features/assets/services/assets.service';
import { categoriesService, Category } from '@/features/assets/services/categories.service';
import { AssetFormDialog } from '@/features/assets/components/AssetFormDialog';
import { AssetHistoryDialog } from '@/features/assets/components/AssetHistoryDialog';
import { AssetImportDialog } from '@/features/assets/components/AssetImportDialog';
import { AssetDetailsDialog } from '@/features/assets/components/AssetDetailsDialog';
import { AssetCommandsDialog } from '@/features/assets/components/AssetCommandsDialog';
import { assetKitsService } from '@/features/assets/services/asset-kits.service';

export interface AssetsSearch {
  search?: string;
  category?: string;
  kit?: string;
  sortBy?: 'name' | 'code';
  sortOrder?: 'desc'; // 'asc' é o padrão e fica fora da URL
  page?: number;
}

export const validateAssetsSearch = (s: Record<string, unknown>): AssetsSearch => ({
  search: optStr(s.search),
  category: optStr(s.category),
  kit: optStr(s.kit),
  sortBy: optEnum(s.sortBy, ['name', 'code'] as const),
  sortOrder: optEnum(s.sortOrder, ['desc'] as const),
  page: optPage(s.page),
});

export const Route = createFileRoute('/_authenticated/assets')({
  validateSearch: validateAssetsSearch,
  component: AssetsPage,
});

function AssetsPage() {
  const { hasPermission } = usePermissions();
  const notification = useNotification();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const urlSearch = Route.useSearch();
  const navigate = Route.useNavigate();
  const page = urlSearch.page ?? 1;
  const [limit] = useState(10);
  const search = urlSearch.search ?? '';
  const categoryId = urlSearch.category ?? '';
  const kitId = urlSearch.kit ?? '';
  const sortBy = urlSearch.sortBy;
  const sortOrder: 'asc' | 'desc' = urlSearch.sortOrder ?? 'asc';
  const [categorySearch, setCategorySearch] = useState('');
  const [kitSearch, setKitSearch] = useState('');
  // qualquer mudança de filtro/ordem volta para a página 1 (page entra no patch só ao paginar)
  const setParams = useCallback(
    (patch: Partial<AssetsSearch>) =>
      navigate({ search: (prev) => ({ ...prev, page: undefined, ...patch }), replace: true }),
    [navigate],
  );
  const setSearch = useCallback(
    (value: string) => {
      if (value.trim() !== search) setParams({ search: value.trim() || undefined });
    },
    [search, setParams],
  );
  const [formOpen, setFormOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [commandsOpen, setCommandsOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('table');

  const canCreate = hasPermission('assets.create');
  const canEdit = hasPermission('assets.edit');
  const canRunCommands = hasPermission('assets.commands');
  const canViewValue = canEdit || canCreate;

  // Buscar categorias
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const response = await categoriesService.getAll();
      // response.data agora sempre será um array (garantido pelo BaseService)
      return Array.isArray(response.data) ? response.data : [];
    },
  });

  const categories: Category[] = categoriesData || [];

  // Buscar assets
  const { data: assetsData, isLoading } = useQuery({
    queryKey: ['assets', page, limit, search, categoryId, kitId, sortBy, sortOrder],
    queryFn: async () => {
      return await assetsService.getAllPaginated({
        page,
        limit,
        search: search || undefined,
        categoryId: categoryId || undefined,
        kitId: kitId || undefined,
        sortBy: sortBy,
        sortOrder: sortOrder,
      });
    },
  });

  const { data: kits } = useQuery({
    queryKey: ['asset-kits-simple-assets', kitSearch],
    queryFn: () => assetKitsService.getSimple(kitSearch || undefined),
  });

  const assets = assetsData?.items || [];
  const total = assetsData?.total || 0;

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: (id: string) => assetsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      notification.success('Equipamento excluído com sucesso');
    },
    onError: () => {
      // Erro já será mostrado pelo interceptor do axios
    },
  });

  const handleDelete = async (asset: Asset) => {
    const ok = await confirm({
      title: `Excluir "${asset.name}"?`,
      description: 'Esta ação não pode ser desfeita.',
      confirmLabel: 'Excluir equipamento',
      destructive: true,
    });
    if (!ok) return;

    try {
      await deleteMutation.mutateAsync(asset.id);
    } catch {
      // Erro já será mostrado pelo interceptor do axios
    }
  };

  const handleViewHistory = (asset: Asset) => {
    setSelectedAsset(asset);
    setHistoryOpen(true);
  };

  const handleOpenCommands = (asset: Asset) => {
    setSelectedAsset(asset);
    setCommandsOpen(true);
  };

  const handleViewDetails = (asset: Asset) => {
    setSelectedAsset(asset);
    setDetailsOpen(true);
  };

  const handleEdit = (asset: Asset) => {
    setSelectedAsset(asset);
    setFormOpen(true);
  };

  const handleCreate = () => {
    setSelectedAsset(null);
    setFormOpen(true);
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['assets'] });
  };

  const handleClearFilters = () => {
    navigate({ search: {}, replace: true });
    setKitSearch('');
  };

  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(categorySearch.toLowerCase()),
  );

  const handleSort = (field: 'name' | 'code') => {
    if (sortBy === field) {
      // Se já está ordenando por este campo, inverte a ordem
      setParams({ sortOrder: sortOrder === 'asc' ? 'desc' : undefined });
    } else {
      // Se é um novo campo, ordena por ele em ordem crescente
      setParams({ sortBy: field, sortOrder: undefined });
    }
  };

  const getSortIcon = (field: 'name' | 'code') => {
    if (sortBy !== field) {
      return <ArrowUpDown className="h-4 w-4 ml-1" />;
    }
    return sortOrder === 'asc' ? (
      <ArrowUp className="h-4 w-4 ml-1" />
    ) : (
      <ArrowDown className="h-4 w-4 ml-1" />
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Equipamentos"
        description="Gerencie seus equipamentos"
        actions={
          canCreate && (
            <>
              <Button variant="outline" onClick={() => setImportOpen(true)}>
                <Upload className="mr-2 h-4 w-4" />
                Importar XLSX
              </Button>
              <Button onClick={handleCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Novo equipamento
              </Button>
            </>
          )
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Lista de Equipamentos</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filtros */}
          <div className="mb-4 flex flex-col gap-4 md:flex-row">
            <div className="flex-1">
              <SearchInput
                placeholder="Pesquisar por nome ou descrição..."
                value={search}
                onSearch={setSearch}
              />
            </div>
            <SearchableSelect
              value={categoryId || undefined}
              onValueChange={(value) =>
                setParams({ category: value === 'all' ? undefined : value || undefined })
              }
              searchValue={categorySearch}
              onSearchChange={setCategorySearch}
              searchPlaceholder="Buscar categoria..."
              selectPlaceholder="Todas as categorias"
              allOptionLabel="Todas as categorias"
              options={filteredCategories.map((category) => ({
                value: category.id,
                label: category.name,
              }))}
            />
            <SearchableSelect
              value={kitId || undefined}
              onValueChange={(value) =>
                setParams({ kit: value === 'all' ? undefined : value || undefined })
              }
              searchValue={kitSearch}
              onSearchChange={setKitSearch}
              searchPlaceholder="Buscar kit..."
              selectPlaceholder="Todos os kits"
              allOptionLabel="Todos os kits"
              options={(kits || []).map((kit) => ({
                value: kit.id,
                label: kit.name,
              }))}
            />
            {(search || categoryId || kitId) && (
              <Button variant="outline" onClick={handleClearFilters}>
                Limpar Filtros
              </Button>
            )}
            <ViewModeToggle value={viewMode} onChange={setViewMode} />
          </div>

          {/* Tabela */}
          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground">Carregando...</div>
          ) : assets.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              Nenhum equipamento encontrado
            </div>
          ) : (
            <>
              {viewMode === 'table' ? (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>
                          <button
                            type="button"
                            onClick={() => handleSort('code')}
                            className="flex items-center hover:text-primary"
                          >
                            Código
                            {getSortIcon('code')}
                          </button>
                        </TableHead>
                        <TableHead>
                          <button
                            type="button"
                            onClick={() => handleSort('name')}
                            className="flex items-center hover:text-primary"
                          >
                            Nome
                            {getSortIcon('name')}
                          </button>
                        </TableHead>
                        <TableHead>Categoria</TableHead>
                        <TableHead>Quantidade</TableHead>
                        <TableHead>Disponível</TableHead>
                        <TableHead>Localização</TableHead>
                        {canViewValue && <TableHead>Valor</TableHead>}
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {assets.map((asset) => {
                        const category = categories.find((c) => c.id === asset.categoryId);
                        return (
                          <TableRow key={asset.id}>
                            <TableCell>{asset.code || '-'}</TableCell>
                            <TableCell className="font-medium">{asset.name}</TableCell>
                            <TableCell>{category?.name || '-'}</TableCell>
                            <TableCell>{asset.quantity}</TableCell>
                            <TableCell>
                              {asset.available !== undefined ? asset.available : asset.quantity}
                            </TableCell>
                            <TableCell>{asset.location || '-'}</TableCell>
                            {canViewValue && (
                              <TableCell>
                                {asset.value
                                  ? `R$ ${parseFloat(asset.value).toLocaleString('pt-BR', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}`
                                  : '-'}
                              </TableCell>
                            )}
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleViewDetails(asset)}
                                  title="Ver detalhes"
                                  aria-label={`Ver detalhes de ${asset.name}`}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleViewHistory(asset)}
                                  title="Ver histórico"
                                  aria-label={`Ver histórico de ${asset.name}`}
                                >
                                  <History className="h-4 w-4" />
                                </Button>
                                {canRunCommands && asset.code && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleOpenCommands(asset)}
                                    title="Comandos remotos"
                                  aria-label={`Comandos remotos de ${asset.name}`}
                                  >
                                    <Terminal className="h-4 w-4" />
                                  </Button>
                                )}
                                {canEdit && (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleEdit(asset)}
                                      title="Editar"
                                  aria-label={`Editar ${asset.name}`}
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleDelete(asset)}
                                      title="Excluir"
                                  aria-label={`Excluir ${asset.name}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {assets.map((asset) => {
                    const category = categories.find((c) => c.id === asset.categoryId);
                    return (
                      <Card key={asset.id} className="overflow-hidden">
                        {asset.imageUrl ? (
                          <img
                            src={asset.imageUrl}
                            alt={asset.name}
                            className="h-40 w-full object-cover"
                          />
                        ) : (
                          <div className="h-40 w-full bg-muted flex items-center justify-center text-muted-foreground text-sm">
                            Sem imagem
                          </div>
                        )}
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base">{asset.name}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-1 text-sm">
                          <p><strong>Código:</strong> {asset.code || '-'}</p>
                          <p><strong>Categoria:</strong> {category?.name || '-'}</p>
                          <p><strong>Quantidade:</strong> {asset.quantity}</p>
                          <p>
                            <strong>Disponível:</strong>{' '}
                            {asset.available !== undefined ? asset.available : asset.quantity}
                          </p>
                          <p><strong>Localização:</strong> {asset.location || '-'}</p>
                          {canViewValue && (
                            <p>
                              <strong>Valor:</strong>{' '}
                              {asset.value
                                ? `R$ ${parseFloat(asset.value).toLocaleString('pt-BR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}`
                                : '-'}
                            </p>
                          )}
                          <div className="pt-3 flex flex-wrap gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleViewDetails(asset)}>
                              Ver
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleViewHistory(asset)}>
                              Histórico
                            </Button>
                            {canRunCommands && asset.code && (
                              <Button variant="outline" size="sm" onClick={() => handleOpenCommands(asset)}>
                                Comandos
                              </Button>
                            )}
                            {canEdit && (
                              <>
                                <Button variant="outline" size="sm" onClick={() => handleEdit(asset)}>
                                  Editar
                                </Button>
                                <Button variant="destructive" size="sm" onClick={() => handleDelete(asset)}>
                                  Excluir
                                </Button>
                              </>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Paginação */}
              {total > limit && (
                <div className="mt-4">
                  <Pagination total={total} page={page} limit={limit} onPageChange={(p) => setParams({ page: p > 1 ? p : undefined })} />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Modais */}
      <AssetFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        onSuccess={handleSuccess}
        asset={selectedAsset || undefined}
        categories={categories}
      />

      <AssetImportDialog open={importOpen} onOpenChange={setImportOpen} onSuccess={handleSuccess} />

      <AssetHistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} asset={selectedAsset} />
      <AssetCommandsDialog open={commandsOpen} onOpenChange={setCommandsOpen} asset={selectedAsset} />

      <AssetDetailsDialog
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        asset={selectedAsset}
        onViewHistory={() => {
          setDetailsOpen(false);
          setHistoryOpen(true);
        }}
      />
    </div>
  );
}
